import React, { useState, useEffect, useRef } from 'react'
import { Button } from '../components/ui/Button'

interface OutputFile {
  name: string
  size: number
  modified: number
}

interface ScraperV2Status {
  running: boolean
  pid: number | null
  start_time: string | null
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleString()
}

const ScraperV2Page: React.FC = () => {
  const [status, setStatus] = useState<ScraperV2Status>({ running: false, pid: null, start_time: null })
  const [outputs, setOutputs] = useState<OutputFile[]>([])
  const [logs, setLogs] = useState<string[]>([])
  const [isStarting, setIsStarting] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const logRef = useRef<HTMLPreElement>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/v1/scraper-v2/status', { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        const wasRunning = status.running
        setStatus(data.data)
        // If just finished, refresh outputs
        if (wasRunning && !data.data.running) {
          fetchOutputs()
        }
      }
    } catch (err) {
      console.error('Failed to fetch status:', err)
    }
  }

  const fetchOutputs = async () => {
    try {
      const res = await fetch('/api/v1/scraper-v2/outputs', { credentials: 'include' })
      const data = await res.json()
      if (data.success) setOutputs(data.data)
    } catch (err) {
      console.error('Failed to fetch outputs:', err)
    }
  }

  const fetchLogs = async () => {
    try {
      const res = await fetch('/api/v1/scraper-v2/logs', { credentials: 'include' })
      const data = await res.json()
      if (data.success) setLogs(data.data)
    } catch (err) {
      console.error('Failed to fetch logs:', err)
    }
  }

  const startScraper = async () => {
    setIsStarting(true)
    try {
      const res = await fetch('/api/v1/scraper-v2/start', { method: 'POST', credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setStatus(data.data)
        setLogs([])
      } else {
        alert(data.error || 'Failed to start scraper')
      }
    } catch (err) {
      alert('Failed to start scraper')
    } finally {
      setIsStarting(false)
    }
  }

  const stopScraper = async () => {
    setIsStopping(true)
    try {
      const res = await fetch('/api/v1/scraper-v2/stop', { method: 'POST', credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setStatus({ running: false, pid: null, start_time: null })
        fetchOutputs()
      } else {
        alert(data.error || 'Failed to stop scraper')
      }
    } catch (err) {
      alert('Failed to stop scraper')
    } finally {
      setIsStopping(false)
    }
  }

  const downloadFile = async (filename: string) => {
    try {
      const res = await fetch(`/api/v1/scraper-v2/download/${encodeURIComponent(filename)}`, { credentials: 'include' })
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      alert('Failed to download file')
    }
  }

  const deleteFile = async (filename: string) => {
    if (!window.confirm(`Delete ${filename}?`)) return
    try {
      const res = await fetch(`/api/v1/scraper-v2/outputs/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      const data = await res.json()
      if (data.success) fetchOutputs()
    } catch (err) {
      alert('Failed to delete file')
    }
  }

  // Poll while running
  useEffect(() => {
    if (status.running) {
      intervalRef.current = setInterval(() => {
        fetchStatus()
        fetchLogs()
      }, 3000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.running])

  // Auto-scroll logs
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight
    }
  }, [logs])

  // Initial fetch
  useEffect(() => {
    fetchStatus()
    fetchOutputs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getElapsedTime = (): string => {
    if (!status.start_time) return ''
    const start = new Date(status.start_time).getTime()
    const elapsed = Math.floor((Date.now() - start) / 1000)
    const mins = Math.floor(elapsed / 60)
    const secs = elapsed % 60
    return `${mins}m ${secs}s`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Scraper V2</h1>
        <p className="text-gray-600 mt-1">
          Quick MiningHub scraper — fetches all projects and companies via API. Takes approximately 20-30 minutes to complete.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Status indicator */}
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${status.running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span className={`font-medium ${status.running ? 'text-green-700' : 'text-gray-600'}`}>
                {status.running ? 'Running' : 'Idle'}
              </span>
            </div>

            {/* Running details */}
            {status.running && (
              <div className="text-sm text-gray-500">
                PID: {status.pid} | Elapsed: {getElapsedTime()}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-3">
            {!status.running ? (
              <Button
                onClick={startScraper}
                loading={isStarting}
                variant="success"
              >
                Run Scraper
              </Button>
            ) : (
              <Button
                onClick={stopScraper}
                loading={isStopping}
                variant="error"
              >
                Stop
              </Button>
            )}
            <Button onClick={fetchOutputs} variant="secondary">
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Log Viewer */}
      {logs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Log Output</h2>
          <pre
            ref={logRef}
            className="bg-gray-900 text-green-400 text-xs font-mono p-4 rounded-lg overflow-auto max-h-80"
          >
            {logs.join('\n')}
          </pre>
        </div>
      )}

      {/* Output Files */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Output Files</h2>
        {outputs.length === 0 ? (
          <p className="text-gray-500 text-sm">No output files yet. Run the scraper to generate files.</p>
        ) : (
          <div className="space-y-2">
            {outputs.map((file, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)} &bull; {formatDate(file.modified)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => downloadFile(file.name)}
                      variant="secondary"
                      className="text-xs px-2 py-1"
                    >
                      Download
                    </Button>
                    <button
                      onClick={() => deleteFile(file.name)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold px-1 py-1 rounded hover:bg-red-50 transition-colors"
                      title="Delete file"
                    >
                      &times;
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ScraperV2Page
